import React, { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import { Building2 } from "lucide-react";
import { CustomPhoneInput } from "../../shared/PhoneInput";

interface HotelDetails {
  id: string;
  hotel_name: string;
  category: string;
  address: string;
  city: string;
  country: string;
  registration_number: string;
  contact_email: string;
  contact_phone: string;
  created_at: string;
  updated_at: string;
}

export function HotelDetails() {
  const [details, setDetails] = useState<HotelDetails | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<HotelDetails>>({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHotelDetails();
  }, []);

  const loadHotelDetails = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setError("User not authenticated");
        return;
      }

      const { data, error: queryError } = await supabase
        .from("hotel_details")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();

      if (queryError && queryError.code !== "PGRST116") {
        throw queryError;
      }

      setDetails(data);
      setFormData(data || {});
    } catch (err) {
      setError("Failed to load hotel details");
      console.error("Error loading details:", err);
    } finally {
      setLoading(false);
    }
  };

  const validateSIRET = (siret: string): boolean => {
    return /^[0-9]{14}$/.test(siret);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setError("User not authenticated");
        return;
      }

      // Validate required fields
      const requiredFields = [
        "hotel_name",
        "address",
        "city",
        "country",
        "registration_number",
        "contact_email",
        "contact_phone",
        "category",
      ] as const;
      for (const field of requiredFields) {
        if (!formData[field]?.trim()) {
          setError(`${field.replace("_", " ").toUpperCase()} is required`);
          return;
        }
      }

      // Validate SIRET for France
      if (
        formData.country === "France" &&
        !validateSIRET(formData.registration_number || "")
      ) {
        setError(
          "For France, registration number must be a valid SIRET (14 digits)"
        );
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.contact_email || "")) {
        setError("Please enter a valid email address");
        return;
      }

      const { error: upsertError } = await supabase
        .from("hotel_details")
        .upsert({
          id: user.id,
          ...formData,
          updated_at: new Date().toISOString(),
        });

      if (upsertError) throw upsertError;

      setIsEditing(false);
      await loadHotelDetails();
    } catch (err) {
      setError("Failed to save hotel details");
      console.error("Error saving details:", err);
    }
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="px-6 py-4 text-center text-sm text-gray-500">
            Loading hotel details...
          </div>
        </div>
      </div>
    );
  }

  const countries = [
    "France",
    "Germany",
    "Italy",
    "Spain",
    "United Kingdom",
    "United States",
  ].sort();

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Building2 className="h-6 w-6 text-blue-500 mr-2" />
            <h2 className="text-xl font-bold text-gray-900">Hotel Details</h2>
          </div>
          {!isEditing && (
            <button onClick={() => setIsEditing(true)} className="btn-primary">
              Edit Details
            </button>
          )}
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Hotel Name</label>
              <input
                type="text"
                value={formData.hotel_name || ""}
                onChange={(e) =>
                  setFormData({ ...formData, hotel_name: e.target.value })
                }
                className="form-input"
                required
              />
            </div>

            <div>
              <label className="form-label">Category</label>
              <select
                value={formData.category || ""}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                className="form-input"
                required
              >
                <option value="">Select Category</option>
                <option value="1">⭐ 1 Star</option>
                <option value="2">⭐⭐ 2 Stars</option>
                <option value="3">⭐⭐⭐ 3 Stars</option>
                <option value="4">⭐⭐⭐⭐ 4 Stars</option>
                <option value="5">⭐⭐⭐⭐⭐ 5 Stars</option>
              </select>
            </div>

            <div>
              <label className="form-label">Address</label>
              <input
                type="text"
                value={formData.address || ""}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                className="form-input"
                required
              />
            </div>

            <div>
              <label className="form-label">Contact Email</label>
              <input
                type="email"
                value={formData.contact_email || ""}
                onChange={(e) =>
                  setFormData({ ...formData, contact_email: e.target.value })
                }
                className="form-input"
                required
              />
            </div>

            <div>
              <label className="form-label">Contact Phone</label>
              <CustomPhoneInput
                value={formData.contact_phone || ""}
                onChange={(value) =>
                  setFormData({ ...formData, contact_phone: value || "" })
                }
                required
              />
            </div>

            <div>
              <label className="form-label">City</label>
              <input
                type="text"
                value={formData.city || ""}
                onChange={(e) =>
                  setFormData({ ...formData, city: e.target.value })
                }
                className="form-input"
                required
              />
            </div>

            <div>
              <label className="form-label">Country</label>
              <select
                value={formData.country || ""}
                onChange={(e) =>
                  setFormData({ ...formData, country: e.target.value })
                }
                className="form-input"
                required
              >
                <option value="">Select Country</option>
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">
                {formData.country === "France"
                  ? "SIRET Number"
                  : "Registration Number"}
              </label>
              <input
                type="text"
                value={formData.registration_number || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    registration_number: e.target.value,
                  })
                }
                className={`form-input ${
                  formData.country === "France" &&
                  formData.registration_number &&
                  !validateSIRET(formData.registration_number)
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
                placeholder={
                  formData.country === "France"
                    ? "14 digits SIRET number"
                    : "Company registration number"
                }
                pattern={
                  formData.country === "France" ? "[0-9]{14}" : undefined
                }
                required
              />
              {formData.country === "France" && (
                <p
                  className={`mt-1 text-sm ${
                    formData.registration_number &&
                    !validateSIRET(formData.registration_number)
                      ? "text-red-600"
                      : "text-gray-500"
                  }`}
                >
                  SIRET must be exactly 14 digits
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setError("");
                  setFormData(details || {});
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                Save Changes
              </button>
            </div>
          </form>
        ) : details ? (
          <div className="space-y-6">
            {/* Hotel Information Section */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">
                  Hotel Name
                </h3>
                <p className="mt-1 text-sm text-gray-900">
                  {details.hotel_name}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">Category</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {"⭐".repeat(Number(details.category))} ({details.category}{" "}
                  {details.category === "1" ? "Star" : "Stars"})
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">Address</h3>
                <p className="mt-1 text-sm text-gray-900">{details.address}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">City</h3>
                <p className="mt-1 text-sm text-gray-900">{details.city}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">Country</h3>
                <p className="mt-1 text-sm text-gray-900">{details.country}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">
                  {details.country === "France"
                    ? "SIRET Number"
                    : "Registration Number"}
                </h3>
                <p className="mt-1 text-sm text-gray-900">
                  {details.registration_number}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">
                  Contact Email
                </h3>
                <p className="mt-1 text-sm text-gray-900">
                  {details.contact_email}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">
                  Contact Phone
                </h3>
                <p className="mt-1 text-sm text-gray-900">
                  {details.contact_phone}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">
              No hotel details configured yet. Add your hotel information to get
              started.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
